import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Package, Sparkles, Store as StoreIcon, LayoutGrid, Bot, Network, GitBranch, Boxes } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import ThemeToggle from "@/components/common/ThemeToggle";
import LanguageToggle from "@/components/common/LanguageToggle";
import AiScore from "@/components/common/AiScore";
import KnowledgeGraphView from "@/components/common/KnowledgeGraphView";
import CodeBlock from "@/components/common/CodeBlock";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4 text-brand" />{title}
      </h3>
      {children}
    </section>
  );
}

export default function StorePreview() {
  const { id } = useParams();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cat, setCat] = useState("all");
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("store");

  useEffect(() => {
    api.get(`/public/stores/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" /></div>;
  if (error || !data) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">{t("preview.notFound")}</div>;

  const { store, products, categories, knowledge, schema, jsonld } = data;
  const filtered = cat === "all" ? products : products.filter((p) => p.category === cat);
  const money = (p) => `${store.currency} ${Number(p.price).toFixed(2)}`;
  const scored = products.filter((p) => p.ai);
  const summary = store.company?.description
    || t("preview.summaryTemplate", { store: store.name, category: store.category || "products", count: products.length });

  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-40 border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            {store.logo_url ? (
              <img src={store.logo_url} alt={store.name} className="h-9 w-9 rounded-md border border-border object-cover" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary font-display font-bold text-primary-foreground">{store.name?.charAt(0)}</div>
            )}
            <div>
              <p className="font-display font-bold leading-none">{store.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{store.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle /><ThemeToggle />
          </div>
        </div>
      </header>

      {/* View toggle */}
      <div className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center gap-1 px-6">
          <button onClick={() => setView("store")} data-testid="view-store"
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${view === "store" ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <LayoutGrid className="h-4 w-4" />{t("preview.storefront")}
          </button>
          <button onClick={() => setView("ai")} data-testid="view-ai"
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${view === "ai" ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Bot className="h-4 w-4" />{t("preview.aiView")}
            <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] text-brand">AI</span>
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {view === "store" ? (
          <>
            {store.company?.description && (
              <p className="mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">{store.company.description}</p>
            )}
            {categories?.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-2" data-testid="preview-categories">
                <button onClick={() => setCat("all")} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${cat === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>{t("preview.all")}</button>
                {categories.map((c) => (
                  <button key={c} onClick={() => setCat(c)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>{c}</button>
                ))}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground">{t("preview.noProducts")}</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="preview-products">
                {filtered.map((p) => (
                  <button key={p.id} onClick={() => setDetail(p)} data-testid={`preview-product-${p.id}`}
                    className="group overflow-hidden rounded-lg border border-border bg-card text-left transition-transform hover:-translate-y-1">
                    <div className="aspect-square overflow-hidden bg-secondary">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full items-center justify-center"><Package className="h-10 w-10 text-muted-foreground" /></div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground">{p.brand}</p>
                      <h3 className="mt-0.5 line-clamp-2 font-medium leading-snug">{p.name}</h3>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="font-mono font-bold">{money(p)}</span>
                        {p.ai && <span className="font-mono text-xs text-brand">AI {p.ai.ai_score}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-6" data-testid="preview-ai-view">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-brand" />{t("preview.aiViewIntro")}
            </div>

            <Panel icon={Bot} title={t("preview.aiSummary")} testid="ai-summary">
              <p className="text-sm leading-relaxed">{summary}</p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span className="font-mono text-muted-foreground">products: <span className="text-foreground">{products.length}</span></span>
                <span className="font-mono text-muted-foreground">categories: <span className="text-foreground">{categories.length}</span></span>
                <span className="font-mono text-muted-foreground">ai_indexed: <span className="text-brand">{scored.length}</span></span>
              </div>
            </Panel>

            <Panel icon={Network} title={t("preview.knowledgeGraph")} testid="ai-graph">
              <KnowledgeGraphView graph={knowledge} />
            </Panel>

            <div className="grid gap-6 md:grid-cols-2">
              <Panel icon={GitBranch} title={t("preview.semanticRelations")} testid="ai-relations">
                <ul className="space-y-1.5 font-mono text-xs">
                  {knowledge?.edges?.slice(0, 12).map((e, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-foreground">{e.source.split(":").pop()}</span>
                      <span className="text-brand">—{e.relation}→</span>
                      <span className="text-foreground">{e.target.split(":").pop()}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel icon={Boxes} title={t("preview.productEntities")} testid="ai-entities">
                <div className="flex flex-wrap gap-2">
                  {products.map((p) => (
                    <span key={p.id} className="rounded-md border border-border bg-secondary/50 px-2.5 py-1 font-mono text-xs">{p.name}</span>
                  ))}
                </div>
              </Panel>
            </div>

            <Panel icon={Boxes} title={t("preview.jsonLd")} testid="ai-jsonld">
              <CodeBlock code={jsonld} language="json-ld" testid="preview-jsonld-block" />
            </Panel>

            <Panel icon={Boxes} title={t("preview.structuredData")} testid="ai-schema">
              <CodeBlock code={schema} language="json" testid="preview-schema-block" />
            </Panel>
          </div>
        )}
      </main>

      <footer className="border-t border-border py-8 text-center">
        <p className="text-xs text-muted-foreground">{t("preview.backToStore")}</p>
      </footer>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto p-0 sm:max-w-3xl" data-testid="preview-detail">
          {detail && (
            <>
              <div className="grid md:grid-cols-2">
                <div className="flex min-h-[280px] items-center justify-center bg-secondary/50 p-6 md:min-h-[420px]">
                  {detail.images?.[0] ? (
                    <img src={detail.images[0]} alt={detail.name} className="max-h-[240px] w-auto object-contain md:max-h-[380px]" />
                  ) : (
                    <Package className="h-16 w-16 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col p-6">
                  <DialogHeader className="text-left">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{detail.brand} · {detail.category}</p>
                    <DialogTitle className="font-display text-2xl tracking-tight">{detail.name}</DialogTitle>
                    <DialogDescription className="sr-only">{detail.name} — {detail.brand}</DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <span className="font-mono text-2xl font-bold">{money(detail)}</span>
                    {detail.ai && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("preview.aiScore")}</span>
                        <AiScore score={detail.ai.ai_score} size="sm" />
                      </div>
                    )}
                  </div>
                  {detail.availability && (
                    <span className={`mt-4 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${detail.availability === "in_stock" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${detail.availability === "in_stock" ? "bg-success" : "bg-warning"}`} />
                      {detail.availability === "in_stock" ? t("products.inStock") : detail.availability === "preorder" ? t("products.preorder") : t("products.outOfStock")}
                    </span>
                  )}
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{detail.ai?.ai_description || detail.description}</p>
                </div>
              </div>
              {detail.ai?.faq?.length > 0 && (
                <div className="border-t border-border p-6">
                  <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">{t("preview.productFaq")}</h4>
                  <div className="space-y-2">
                    {detail.ai.faq.map((f, i) => (
                      <div key={i} className="rounded-md border border-border p-3">
                        <p className="text-sm font-semibold">{f.q}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
